import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { FormDataItem, InputDataItem, FormItem } from '@/entries/interact-form/types'
import { extractPrefix, removePrefix } from '@/entries/interact-form/constants'

export const useFormDataStore = defineStore('formData', () => {
  // フォームデータを保持するref
  const formItems = ref<FormDataItem[]>([])

  // contactformのIDを保持するref
  const contactFormId = ref<string>('')

  // フォームの詳細情報を保持するref
  const formDetails = ref<FormItem[]>([])

  // 現在の質問インデックス
  const currentQuestionIndex = ref(0)

  // 最後の質問に回答したかどうかのフラグ
  const hasCompletedAllQuestions = ref(false)

  // 現在の質問
  const currentQuestion = computed(() => {
    return formDetails.value.length > 0 ? formDetails.value[currentQuestionIndex.value] : null
  })

  // 質問の総数
  const totalQuestions = computed(() => formDetails.value.length)

  // 回答が入力されているかチェック
  const isAnswerEmpty = computed(() => {
    if (!currentQuestion.value) return true

    if (Array.isArray(currentQuestion.value.answer)) {
      return currentQuestion.value.answer.length === 0
    }

    return !currentQuestion.value.answer
  })

  // ボタンの無効状態を計算
  const isNextButtonDisabled = computed(() => {
    if (!currentQuestion.value) return true
    return currentQuestion.value.required && isAnswerEmpty.value
  })

  // 次の質問に進む
  function nextQuestion() {
    console.log('[FormDataStore] nextQuestion called', {
      currentIndex: currentQuestionIndex.value,
      totalQuestions: totalQuestions.value,
      hasCompletedAllQuestions: hasCompletedAllQuestions.value
    })
    
    if (currentQuestionIndex.value < totalQuestions.value - 1) {
      currentQuestionIndex.value++
      console.log('[FormDataStore] Moved to question index:', currentQuestionIndex.value)
    } else {
      // 最後の質問に回答した場合
      hasCompletedAllQuestions.value = true
      console.log('[FormDataStore] All questions completed, showing contact form')
    }
  }

  // 前の質問に戻る
  function prevQuestion() {
    console.log('[FormDataStore] prevQuestion called', {
      currentIndex: currentQuestionIndex.value,
      hasCompletedAllQuestions: hasCompletedAllQuestions.value
    })
    
    // Contact Formから戻る場合
    if (hasCompletedAllQuestions.value) {
      hasCompletedAllQuestions.value = false
      // 最後の質問に戻る
      currentQuestionIndex.value = totalQuestions.value - 1
      console.log('[FormDataStore] Returned from contact form to question index:', currentQuestionIndex.value)
    } else if (currentQuestionIndex.value > 0) {
      currentQuestionIndex.value--
      console.log('[FormDataStore] Moved back to question index:', currentQuestionIndex.value)
    } else {
      console.log('[FormDataStore] Already at first question')
    }
  }

  // contactFormIdを設定する関数
  function setContactFormId(id: string) {
    contactFormId.value = id
  }

  // 入力データからフォームデータを初期化する関数
  function initializeFromInputData(inputData: InputDataItem[], formId: string) {
    if (!inputData || !Array.isArray(inputData) || inputData.length === 0) {
      console.error('Invalid or empty input data')
      return
    }

    try {
      // contactFormIdを設定
      setContactFormId(formId)

      // 重複IDを検出し、最初の設問のみを保持する
      const seenIds = new Set<number>() // 既に処理したID
      const uniqueInputData: InputDataItem[] = []

      // 重複を除外した入力データを作成
      inputData.forEach((item) => {
        if (!seenIds.has(item.id)) {
          // 初めて見るIDの場合は追加
          seenIds.add(item.id)
          uniqueInputData.push(item)
        } else {
          // 重複IDを検出した場合はスキップ
          console.warn(`重複したID ${item.id} を検出しました。この設問はスキップします。`, item)
        }
      })

      // 重複を除外した後の設問数をログ出力
      console.log(`元の設問数: ${inputData.length}, 重複除外後の設問数: ${uniqueInputData.length}`)

      // 基本データの作成（重複を除外したデータを使用）
      const basicData = uniqueInputData.map((item) => ({
        id: item.id,
        name: item.name || `question_${item.id}`,
        value: '', // 初期値は空
      }))

      // 詳細データの作成（重複を除外したデータを使用）
      const detailsData = uniqueInputData
        .map((item) => {
          try {
            // 必要なプロパティが存在するか確認
            if (!item || !item.type || !item.data) {
              console.warn('Skipping item with missing properties:', item)
              return null
            }

            // FormItem形式に変換
            const formItem: FormItem = {
              id: item.id,
              label: item.label || '',
              question: item.question || item.label || '',
              type: item.type,
              data: Array.isArray(item.data) ? item.data : [],
              required: item.required || false,
              answer: item.type === 'check_box' ? [] : '',
              is_trigger: item.is_trigger || false,
              related_service: item.related_service,
              exclude_services: item.exclude_services,
              selectedService: {},
            }

            return formItem
          } catch (e) {
            console.error('Failed to process item:', item, e)
            return null
          }
        })
        .filter((item): item is FormItem => item !== null)

      // ストアのデータを初期化
      initializeData(basicData, detailsData)

      console.log('Form data initialized:', {
        contactFormId: contactFormId.value,
        basicData,
        detailsData,
      })

      return {
        basicData,
        detailsData,
      }
    } catch (e) {
      console.error('Error initializing form data:', e)
      return null
    }
  }

  // データを初期化する関数
  function initializeData(basicData: FormDataItem[], detailsData: FormItem[]) {
    formItems.value = basicData
    formDetails.value = detailsData
    currentQuestionIndex.value = 0
    hasCompletedAllQuestions.value = false
  }

  // データを追加する関数
  function addItem(item: FormDataItem) {
    formItems.value.push(item)
  }

  // データを更新する関数
  function updateItem(id: number, updatedItem: Partial<FormDataItem>) {
    const index = formItems.value.findIndex((item) => item.id === id)
    if (index !== -1) {
      formItems.value[index] = { ...formItems.value[index], ...updatedItem }
    }
  }

  // 回答を更新する関数
  function updateAnswer(id: number, answer: string | string[]) {
    console.log('[FormDataStore] updateAnswer called', {
      questionId: id,
      answer,
      currentQuestionIndex: currentQuestionIndex.value
    })
    
    // 詳細データの更新
    const detailIndex = formDetails.value.findIndex((item) => item.id === id)
    if (detailIndex !== -1) {
      formDetails.value[detailIndex].answer = answer
      console.log('[FormDataStore] Answer updated for question:', id)

      // is_triggerがtrueの場合、関連サービスを保持
      if (
        formDetails.value[detailIndex].is_trigger &&
        formDetails.value[detailIndex].related_service
      ) {
        const answerStr = Array.isArray(answer) ? answer[0] : answer
        // データ配列から選択された回答のインデックスを取得
        const answerIndex = formDetails.value[detailIndex].data.findIndex(
          (item) => item === answerStr,
        )

        // インデックスが見つかった場合、対応するサービスを選択
        if (answerIndex !== -1) {
          const relatedService = formDetails.value[detailIndex].related_service
          const selectedService: Record<string, number[]> = {}

          // 任意のアルファベットプレフィックスをサポート
          if (relatedService) {
            // relatedServiceのすべてのキーをループ処理
            Object.keys(relatedService).forEach((key) => {
              const value = relatedService[key]
              // 配列の場合のみ処理
              if (Array.isArray(value) && value.length > answerIndex) {
                selectedService[key] = [value[answerIndex]]
              }
            })
          }

          formDetails.value[detailIndex].selectedService = selectedService
        }
      }
    }

    // 基本データの更新
    const value = Array.isArray(answer) ? answer.join(',') : answer
    updateItem(id, { value })
  }

  // データを削除する関数
  function removeItem(id: number) {
    formItems.value = formItems.value.filter((item) => item.id !== id)
  }

  // メール送信データ処理
  function processFormData() {
    // 現在のページタイトルを取得（グローバル変数から取得）
    const pageTitle = window.getBizcanPageTitle
      ? window.getBizcanPageTitle()
      : window.document.title || ''

    // トリガー設問と選択されたサービスの情報を収集
    const triggerQuestionsData = formDetails.value
      .filter((item) => item.is_trigger && item.selectedService)
      .map((item) => {
        // 選択された回答を取得（配列または単一値）
        const answers = Array.isArray(item.answer) ? item.answer : [item.answer || '']

        // 各回答のプレフィックスと表示用テキストを処理
        const processedAnswers = answers.map((ans) => {
          const prefix = extractPrefix(ans)
          const display = ans ? removePrefix(ans) : ans
          return { original: ans, prefix, display }
        })

        // サービスIDを取得（選択された回答の先頭文字に対応するもの）
        const serviceIds: Record<string, number[]> = {}

        // 各回答のプレフィックスに基づいてサービスIDを収集
        processedAnswers.forEach((ans) => {
          if (ans.prefix && item.selectedService) {
            const prefix = ans.prefix

            // 複数文字のプレフィックスを処理（例：'ab'の場合、'a'と'b'の両方のサービスIDを取得）
            if (prefix.length > 1) {
              // 各文字を個別に処理
              for (let i = 0; i < prefix.length; i++) {
                const singlePrefix = prefix[i]
                if (item.selectedService[singlePrefix]) {
                  serviceIds[singlePrefix] = serviceIds[singlePrefix] || []
                  serviceIds[singlePrefix] = [
                    ...(serviceIds[singlePrefix] || []),
                    ...(item.selectedService[singlePrefix] || []),
                  ]
                }
              }
            }
            // 単一文字のプレフィックスの場合（従来の処理）
            else if (item.selectedService[prefix]) {
              serviceIds[prefix] = serviceIds[prefix] || []
              serviceIds[prefix] = [
                ...(serviceIds[prefix] || []),
                ...(item.selectedService[prefix] || []),
              ]
            }
          }
        })

        // 重複を排除
        Object.keys(serviceIds).forEach((key) => {
          if (serviceIds[key]) {
            serviceIds[key] = [...new Set(serviceIds[key])]
          }
        })

        // プレフィックスの存在確認（少なくとも1つの回答にプレフィックスがあるか）

        return {
          questionId: item.id,
          question: item.question,
          answer: answers.join(', '),
          displayAnswer: processedAnswers.map((a) => a.display).join(', '),
          answerPrefix: processedAnswers.some((a) => a.prefix !== null)
            ? processedAnswers[0].prefix
            : null,
          serviceIds: serviceIds,
          // 複数選択用の追加情報
          isMultipleAnswers: answers.length > 1,
          originalAnswers: answers,
          processedAnswers: processedAnswers,
        }
      })

    // トリガー設問と選択サービスの情報をテキストで整形（プレーンテキスト版 - エスケープ済み）
    const triggersText = triggerQuestionsData
      .map((item) => {
        const serviceIdsText =
          Object.entries(item.serviceIds).length > 0
            ? Object.entries(item.serviceIds)
                .map(([key, ids]) => `${key}: ${ids.join(', ')}`)
                .join('; ')
            : '関連サービスなし'

        // 複数選択の場合は各回答の詳細情報を表示
        let answersText = ''
        if (item.isMultipleAnswers) {
          answersText = item.processedAnswers
            .map((ans) => {
              const originalText = ans.prefix ? `(元の値: ${ans.original})` : ''
              return `- ${ans.display} ${originalText}`
            })
            .join('\n')
        } else {
          const originalValueText = item.answerPrefix ? `(元の値: ${item.answer})` : ''
          answersText = `${item.displayAnswer} ${originalValueText}`
        }

        return `トリガー設問: ${item.question}\n選択された回答:\n${answersText}\n関連サービスID: ${serviceIdsText}`
      })
      .join('\n\n')

    // HTML版のトリガー情報（メール用）
    const triggersHtml = triggerQuestionsData
      .map((item) => {
        const serviceIdsText =
          Object.entries(item.serviceIds).length > 0
            ? Object.entries(item.serviceIds)
                .map(([key, ids]) => `${key}: ${ids.join(', ')}`)
                .join('; ')
            : '関連サービスなし'

        // 複数選択の場合は各回答の詳細情報を表示
        let answersHtml = ''
        if (item.isMultipleAnswers) {
          answersHtml =
            '<ul>' +
            item.processedAnswers
              .map((ans) => {
                const originalText = ans.prefix ? `(元の値: ${ans.original})` : ''
                return `<li>${ans.display} ${originalText}</li>`
              })
              .join('') +
            '</ul>'
        } else {
          const originalValueText = item.answerPrefix ? `(元の値: ${item.answer})` : ''
          answersHtml = `${item.displayAnswer} ${originalValueText}`
        }

        return `<p><strong>トリガー設問:</strong> ${item.question}</p>
<p><strong>選択された回答:</strong></p>
${answersHtml}
<p><strong>関連サービスID:</strong> ${serviceIdsText}</p>`
      })
      .join('<hr>')

    // メール用のプレーンテキスト版（改行はそのまま、HTMLタグなし）
    const triggersPlainText = triggerQuestionsData
      .map((item) => {
        const serviceIdsText =
          Object.entries(item.serviceIds).length > 0
            ? Object.entries(item.serviceIds)
                .map(([key, ids]) => `${key}: ${ids.join(', ')}`)
                .join('; ')
            : '関連サービスなし'

        // 複数選択の場合は各回答の詳細情報を表示
        let answersText = ''
        if (item.isMultipleAnswers) {
          answersText = item.processedAnswers
            .map((ans) => {
              const originalText = ans.prefix ? `(元の値: ${ans.original})` : ''
              return `- ${ans.display} ${originalText}`
            })
            .join('\n')
        } else {
          const originalValueText = item.answerPrefix ? `(元の値: ${item.answer})` : ''
          answersText = `${item.displayAnswer} ${originalValueText}`
        }

        return `トリガー設問: ${item.question}
選択された回答:
${answersText}
関連サービスID: ${serviceIdsText}`
      })
      .join('\n\n')

    // デフォルトテキスト設定
    const finalTriggersText = triggersText || 'トリガー設問による選択はありません'
    const finalTriggersHtml = triggersHtml || '<p>トリガー設問による選択はありません</p>'
    const finalTriggersPlainText = triggersPlainText || 'トリガー設問による選択はありません'

    // 設問と回答のテキストを生成（HTMLタグを使用）
    const formattedHtml = formDetails.value
      .map((item) => {
        let answer = Array.isArray(item.answer) ? item.answer.join(', ') : item.answer || '未回答'

        // トリガー設問の場合は、表示用の回答テキストを使用
        if (item.is_trigger && typeof answer === 'string' && answer) {
          answer = removePrefix(answer)
        }

        return `<p><strong>${item.question}:</strong> ${answer}</p>`
      })
      .join('')

    // プレーンテキスト版も生成（改行を含む）
    const plainText = formDetails.value
      .map((item) => {
        let answer = Array.isArray(item.answer) ? item.answer.join(', ') : item.answer || '未回答'

        // トリガー設問の場合は、表示用の回答テキストを使用
        if (item.is_trigger && typeof answer === 'string' && answer) {
          answer = removePrefix(answer)
        }

        return `${item.question}: ${answer}`
      })
      .join('\n\n')

    // 改行文字をエスケープ（WordPressのメール送信で\nとして処理されるように）
    const escapedText = plainText.replace(/\n/g, '\\n')

    // 元の返り値に加えて、新しい形式のデータも追加
    return {
      contactFormId: contactFormId.value,
      formData: formItems.value,
      questionsAndAnswersText: plainText,
      escapedQuestionsAndAnswersText: escapedText,
      formattedHtml: formattedHtml,
      pageTitle: pageTitle,
      selectedServices: triggerQuestionsData,
      triggersText: finalTriggersText.replace(/\n/g, '\\n'),
      triggersHtml: finalTriggersHtml,
      triggersPlainText: finalTriggersPlainText,
    }
  }

  // フォームをリセットする関数
  function resetForm() {
    formItems.value = formItems.value.map((item) => ({
      ...item,
      value: '',
    }))

    formDetails.value = formDetails.value.map((item) => ({
      ...item,
      answer: item.type === 'check_box' ? [] : '',
      selectedService: {}, // 選択されたサービスもリセット
    }))

    currentQuestionIndex.value = 0
    hasCompletedAllQuestions.value = false
  }

  return {
    formItems,
    formDetails,
    contactFormId,
    currentQuestionIndex,
    currentQuestion,
    totalQuestions,
    hasCompletedAllQuestions,
    isAnswerEmpty,
    isNextButtonDisabled,
    setContactFormId,
    initializeFromInputData,
    initializeData,
    addItem,
    updateItem,
    updateAnswer,
    removeItem,
    processFormData,
    nextQuestion,
    prevQuestion,
    resetForm,
  }
})
